import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import COPY from './copy.json';
import SERVICE_CATALOG from './services.json';
import {
  t, PHONE, PHONE_LABEL, WA, initReveal, Figure,
  IconPhone, IconWa, IconTick, IconArrow, IconStore, IconPlay,
  Header, Footer, CallBar
} from './SiteChrome';

/*
 * Home page situs publik — port dari site/index.html paket redesign client.
 *
 * Header, footer, call bar, ikon dan helper teks kini berada di SiteChrome.js,
 * dipakai bersama halaman Services.
 *
 * Style datang dari public/css/{tokens,site,fonts}.css yang dimuat di Html.js,
 * bukan dari isomorphic-style-loader — supaya CSS ini tidak pernah bocor ke
 * dashboard. Lihat PUBLIC_ROUTES di server.js.
 */

/*
 * Lima layanan = lima kategori asli di DB (Category), bukan lima layanan
 * karangan prototipe. `price` null berarti tidak punya harga tetap: Vehicle
 * Recovery dihitung per jarak. Angka lain diambil dari services.json, yang
 * bersumber dari tabel Pricing.
 */
const cheapest = key => Math.min(...SERVICE_CATALOG[key].items
  .map(i => i.price).filter(p => p != null));

const SERVICES = [
  { key: 'recovery', href: '/service-recovery', price: null, eta: '~25 min',
    icon: <svg viewBox="0 0 24 24"><path d="M2 15V9h9l3-3h4l1 4h3v5h-2" /><path d="M9 15H4" /><circle cx="7" cy="15.3" r="2.2" /><circle cx="18" cy="15.3" r="2.2" /></svg> },
  { key: 'tyre', href: '/service-tyre', price: cheapest('tyre'), eta: '~22 min',
    icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3.2" /><path d="M12 3.5v5M12 15.5v5M3.5 12h5M15.5 12h5" /></svg> },
  { key: 'fuel', href: '/service-fuel', price: cheapest('fuel'), eta: '~25 min',
    icon: <svg viewBox="0 0 24 24"><path d="M6 20.5V5a1.5 1.5 0 0 1 1.5-1.5h6A1.5 1.5 0 0 1 15 5v15.5" /><path d="M4.5 20.5h12" /><path d="M15 9h2.5a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 1.5 1.5" /><path d="M7.5 8h6" /></svg> },
  { key: 'carservices', href: '/service-car-services', price: cheapest('carservices'), eta: '~30 min',
    icon: <svg viewBox="0 0 24 24"><path d="M4 8.5h13v7H4zM17 10.5h3v3h-3" /><path d="M8 10.5l-1.5 5" /><path d="M12.5 10.5L11 15.5" /></svg> },
  { key: 'carwash', href: '/service-car-wash', price: cheapest('carwash'), eta: '~35 min',
    icon: <svg viewBox="0 0 24 24"><path d="M3 16.5V12l2.5-5h13L21 12v4.5" /><path d="M3 16.5h18" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /><path d="M8 4.5c0 1-1 1.4-1 2.5M12 4c0 1-1 1.4-1 2.5M16 4.5c0 1-1 1.4-1 2.5" /></svg> }
];

// [kunci teks, kunci catatan, nilai menit] — angka dipisah dari kalimat supaya
// terjemahan tidak perlu mengulang angkanya.
const COVERAGE = [
  ['cov.dubai', 25], ['cov.abudhabi', 30], ['cov.sharjah', 28], ['cov.ajman', 35],
  ['cov.rak', 40], ['cov.fujairah', 45], ['cov.uaq', 40], ['cov.e11', null]
];

const FAQ_KEYS = [1, 2, 3, 4, 5, 6];

const LD_BUSINESS = {
  '@context': 'https://schema.org',
  '@type': 'AutoRepair',
  '@id': 'https://basrecovery.com/#business',
  name: 'Bas Recovery',
  description: '24/7 car recovery and roadside assistance across the United Arab Emirates.',
  url: 'https://basrecovery.com/',
  telephone: PHONE,
  priceRange: 'AED 120–500',
  areaServed: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']
    .map(name => ({ '@type': 'City', name })),
  address: { '@type': 'PostalAddress', addressCountry: 'AE', addressRegion: 'Dubai' },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '00:00', closes: '23:59'
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog', name: 'Roadside services',
    itemListElement: ['Vehicle recovery and towing', 'Battery jump start and replacement',
      'Tyre change and puncture repair', 'Emergency fuel delivery', 'Vehicle lockout assistance']
      .map(name => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name } }))
  }
};

// Schema sengaja selalu en-US: satu URL = satu bahasa untuk crawler. Kalau
// nanti ada route /ar/, halaman itu yang memuat schema versi Arab.
const LD_FAQ = {
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: FAQ_KEYS.map(n => ({
    '@type': 'Question', name: COPY['en-US']['faq.' + n + '.q'],
    acceptedAnswer: { '@type': 'Answer', text: COPY['en-US']['faq.' + n + '.a'] }
  }))
};

class PublicHome extends React.Component {
  state = { navOpen: false };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  /*
   * Sakelar bahasa. Memakai setLocale bawaan admin-web, yang sudah menyimpan
   * cookie `lang` sendiri sehingga pilihan bertahan antar-halaman.
   *
   * lang diset manual di sini karena atribut itu dirender di server; setLocale
   * hanya memperbarui state redux, tidak menyentuh DOM. `lang` yang memicu
   * aturan html[lang="ar"] di tokens.css (ukuran huruf + line-height Arab) dan
   * memilih Cairo lewat unicode-range di fonts.css.
   *
   * dir SENGAJA dibiarkan "ltr" — permintaan client: teks Arab, layout tetap
   * dari kiri. Lihat komentar di Html.js.
   *
   * CATATAN: ini TIDAK mengubah URL, jadi versi Arab tidak bisa diindeks
   * Google. Untuk itu perlu route /ar/ — lihat docs/arabic-plan.md.
   */
  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  // Esc menutup nav mobile — sepadan dengan handler keydown di site.js prototipe.
  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }


  render() {
    const { navOpen } = this.state;
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current="/"
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          {/* ── HERO ── */}
          <section className="hero">
            <div className="wrap hero-grid">
              <div>
                <p className="eyebrow">{tx('hero.eyebrow')}</p>
                <h1>{tx('hero.h1a')} <em>{tx('hero.h1b')}</em></h1>
                <p className="lead">{tx('hero.lead')}</p>
                <div className="hero-cta">
                  <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                    <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
                  </a>
                  <a className="btn btn-wa btn-lg" href={WA}><IconWa />{tx('hero.whatsapp')}</a>
                </div>
                <p className="hero-note">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s7-3.2 7-9V6l-7-3-7 3v6c0 5.8 7 9 7 9Z" /></svg>
                  {tx('hero.note')}
                </p>
              </div>

              <aside className="eta-card" aria-label={tx('eta.aria')}>
                <div className="eta-big">25<span> {tx('eta.unit')}</span></div>
                <p className="eta-label">{tx('eta.label')}</p>
                <div className="eta-rule" />
                <div className="eta-rows">
                  <div className="eta-row">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" /><path d="M12 7.5V12l3 2" /></svg>
                    <span><b>{tx('eta.row1b')}</b>{tx('eta.row1')}</span>
                  </div>
                  <div className="eta-row"><IconTick /><span><b>{tx('eta.row2b')}</b>{tx('eta.row2')}</span></div>
                  <div className="eta-row">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s6.5-5.6 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15.4 12 21 12 21Z" /><path d="M12 13a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" /></svg>
                    <span><b>{tx('eta.row3b')}</b>{tx('eta.row3')}</span>
                  </div>
                  <div className="eta-row">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8.5h16v11H4z" /><path d="M4 8.5l2.5-4h11L20 8.5" /></svg>
                    <span><b>{tx('eta.row4b')}</b>{tx('eta.row4')}</span>
                  </div>
                </div>
              </aside>
            </div>
          </section>

          {/* ── TRUST ── */}
          <section className="trust" style={{ paddingBlock: 0 }}>
            <div className="wrap trust-grid">
              <div className="trust-item"><div className="trust-num">12,400<em>+</em></div><div className="trust-lbl">{tx('trust.jobs')}</div></div>
              <div className="trust-item"><div className="trust-num">4.8<em>/5</em></div><div className="trust-lbl">{tx('trust.rating')}</div></div>
              <div className="trust-item"><div className="trust-num">7</div><div className="trust-lbl">{tx('trust.emirates')}</div></div>
              <div className="trust-item"><div className="trust-num">54</div><div className="trust-lbl">{tx('trust.drivers')}</div></div>
            </div>
          </section>

          {/* ── SERVICES ── */}
          <section className="band-warm">
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('svc.eyebrow')}</p>
                <h2>{tx('svc.h2')}</h2>
                <p className="lead">{tx('svc.lead')}</p>
              </div>

              <div className="svc-grid">
                {SERVICES.map(sv => (
                  <a className="svc" href={sv.href} key={sv.key}>
                    <span className="svc-ico" aria-hidden="true">{sv.icon}</span>
                    <h3>{tx(`svc.${sv.key}.title`)}</h3>
                    <p>{tx(`svc.${sv.key}.body`)}</p>
                    <div className="svc-meta">
                      <span>{sv.price != null
                        ? <span>{tx('svc.from')} <b>AED {sv.price}</b></span>
                        : <b>{tx('svc.byDistance')}</b>}</span>
                      <span><b>{sv.eta}</b></span>
                    </div>
                    <span className="svc-more">{tx(`svc.${sv.key}.more`)} <IconArrow /></span>
                  </a>
                ))}

                <div className="svc" style={{ background: 'var(--night)', borderColor: 'transparent' }}>
                  <span className="svc-ico" style={{ background: 'rgba(255,255,255,.1)' }} aria-hidden="true">
                    <svg viewBox="0 0 24 24" style={{ stroke: 'var(--orange)' }}><path d="M21 16.9v2.5a1.6 1.6 0 0 1-1.8 1.6 16 16 0 0 1-7-2.5 15.7 15.7 0 0 1-4.8-4.8 16 16 0 0 1-2.5-7.1A1.6 1.6 0 0 1 6.5 4.7H9a1.6 1.6 0 0 1 1.6 1.4c.1.8.3 1.5.6 2.2a1.6 1.6 0 0 1-.4 1.7l-1 1a12.9 12.9 0 0 0 4.8 4.8l1-1a1.6 1.6 0 0 1 1.7-.4c.7.3 1.4.5 2.2.6A1.6 1.6 0 0 1 21 16.9Z" /></svg>
                  </span>
                  <h3 style={{ color: 'var(--on-night)' }}>{tx('svc.unsure.title')}</h3>
                  <p style={{ color: 'var(--on-night-2)' }}>{tx('svc.unsure.body')}</p>
                  <a className="btn btn-call" href={`tel:${PHONE}`} style={{ alignSelf: 'flex-start' }}>{tx('svc.unsure.cta')}</a>
                </div>
              </div>
            </div>
          </section>

          {/* ── HOW IT WORKS ── */}
          <section>
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('steps.eyebrow')}</p>
                <h2>{tx('steps.h2')}</h2>
              </div>
              <div className="steps">
                {[1, 2, 3].map(n => (
                  <div className="step" key={n}>
                    <h3>{tx(`steps.${n}.title`)}</h3>
                    <p>{tx(`steps.${n}.body`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── WHY US ── */}
          <section className="band-warm">
            <div className="wrap split">
              <div>
                <p className="eyebrow">{tx('why.eyebrow')}</p>
                <h2>{tx('why.h2')}</h2>
                <p className="lead">{tx('why.lead')}</p>
                <div className="ticks">
                  {[1, 2, 3, 4].map(n => (
                    <div className="tick" key={n}>
                      <IconTick /><span><strong>{tx(`why.${n}b`)}</strong>{tx(`why.${n}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Figure photo="home" alt={tx('why.figureAlt')} caption={tx('why.figure')} />
            </div>
          </section>

          {/* ── COVERAGE ── */}
          <section>
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('cov.eyebrow')}</p>
                <h2>{tx('cov.h2')}</h2>
                <p className="lead">{tx('cov.lead')}</p>
              </div>
              <div className="cov-grid">
                {COVERAGE.map(([key, mins]) => (
                  <div className="cov" key={key}>
                    <IconTick />
                    <span>{tx(key)}<small>{mins ? tx('cov.median', { n: mins }) : tx('cov.arrangement')}</small></span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 'var(--sp-5)' }}>
                <a className="svc-more" href="/coverage">{tx('cov.more')} <IconArrow /></a>
              </p>
            </div>
          </section>

          {/* ── APPS ── */}
          <section className="band-night">
            <div className="wrap">
              <div className="sec-head">
                <p className="eyebrow">{tx('app.eyebrow')}</p>
                <h2>{tx('app.h2')}</h2>
                <p>{tx('app.lead')}</p>
              </div>
              <div className="app-grid">
                <div className="app">
                  <h3>{tx('app.customer.title')}</h3>
                  <p>{tx('app.customer.body')}</p>
                  <div className="stores">
                    <a className="store" href="https://apps.apple.com/us/app/basrecovery/id6744970584" aria-label="Download the customer app on the App Store"><IconStore />{tx('app.appStore')}</a>
                    <a className="store" href="https://play.google.com/store/apps/details?id=com.nagmalshamalportal.basrecovery" aria-label="Get the customer app on Google Play"><IconPlay />{tx('app.googlePlay')}</a>
                  </div>
                </div>
                <div className="app">
                  <h3>{tx('app.provider.title')}</h3>
                  <p>{tx('app.provider.body')} <a href="/providers" style={{ color: 'var(--orange-hot)', textDecoration: 'underline' }}>{tx('app.provider.link')}</a>.</p>
                  <div className="stores">
                    <a className="store" href="https://apps.apple.com/us/app/bas-recovery-providers/id6744971788" aria-label="Download the provider app on the App Store"><IconStore />{tx('app.appStore')}</a>
                    <a className="store" href="https://play.google.com/store/apps/details?id=com.nagmalshamalportal.basrecoveryproviders" aria-label="Get the provider app on Google Play"><IconPlay />{tx('app.googlePlay')}</a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section>
            <div className="wrap-narrow">
              <div className="sec-head">
                <p className="eyebrow">{tx('faq.eyebrow')}</p>
                <h2>{tx('faq.h2')}</h2>
              </div>
              <div className="faq">
                {FAQ_KEYS.map((n, i) => (
                  <details key={n} open={i === 0}>
                    <summary>{tx(`faq.${n}.q`)}</summary>
                    <div><p>{tx(`faq.${n}.a`)}</p></div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── FINAL CTA ── */}
          <section className="band-sand">
            <div className="wrap" style={{ textAlign: 'center' }}>
              <h2 style={{ maxWidth: '24ch', marginInline: 'auto' }}>{tx('cta.h2')}</h2>
              <p className="lead" style={{ maxWidth: '52ch', margin: 'var(--sp-4) auto var(--sp-6)' }}>{tx('cta.lead')}</p>
              <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}><IconPhone />{PHONE_LABEL}</a>
                <a className="btn btn-ghost btn-lg" href="/contact">{tx('cta.callback')}</a>
              </div>
            </div>
          </section>
        </main>

        <Footer locale={L} />

        {/* Sticky mobile action bar — inti dari redesign ini. */}
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_BUSINESS) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_FAQ) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state?.intl?.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(PublicHome);
